import React, { useState, useEffect, useRef } from 'react';
import { Upload, CheckCircle, FileText, XCircle, Pencil } from 'lucide-react';
import axios from 'axios';

const TDSApplicationPage = ({ closeModal, state }) => {
  const [document, setDocument] = useState(null);
  const [existingUrl, setExistingUrl] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef();

  const fetchExistingFile = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/getTDS/${state.accidentVehicleCode}`);
      console.log("Existing TDS doc response:", res.data.data);
      if (res.data.data?.TDSDoc) {
        setExistingUrl(res.data.data.TDSDoc);
      }
    } catch (error) {
      console.log("No existing TDS doc found or error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExistingFile();
  }, []);

  const handleFileClick = () => fileInputRef.current.click();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDocument(file);
      setExistingUrl(null); // Replacing old file
    }
  };

  const removeFile = () => {
    setDocument(null);
    setIsEditing(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!document) {
      alert('Please upload a document before submitting.');
      return;
    }

    const formData = new FormData();
    formData.append('TDSDoc', document);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/submittingTDS/${state.accidentVehicleCode}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      if (response.data.status === true) {
        closeModal();
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  return (
    <div className="font-satoshi max-w-3xl mx-auto p-6 bg-white rounded-xl">
      <h1 className="text-xl font-bold mb-6 text-center text-green-700">
        TDS Form (26AS)
      </h1>

      {loading ? (
        <p className="text-center text-gray-500">Loading...</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Upload / Preview Section */}
          <div>
            {!existingUrl && !document && (
              <label
                className="flex items-center gap-2 font-medium text-sm cursor-pointer text-green-700"
                onClick={handleFileClick}
              >
                <Upload className="w-5 h-5" />
                Upload Image or PDF
              </label>
            )}

            <input
              type="file"
              ref={fileInputRef}
              accept="image/*,application/pdf"
              onChange={handleFileChange}
              hidden
            />

            {document && (
              <div className="mt-4 flex items-center gap-2 text-sm border p-2 rounded-md">
                {document.type.startsWith('image/') ? (
                  <img
                    src={URL.createObjectURL(document)}
                    alt="preview"
                    className="h-16 w-16 object-cover rounded"
                  />
                ) : (
                  <FileText className="w-6 h-6 text-gray-500" />
                )}
                <span className="flex-1 truncate">{document.name}</span>
                <button
                  type="button"
                  onClick={removeFile}
                  className="text-red-500 hover:text-red-700"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            )}

            {!document && existingUrl && (
              <div className="mt-4 flex items-center gap-3 text-sm border p-2 rounded-md">
                {existingUrl.endsWith('.pdf') ? (
                  <FileText className="w-6 h-6 text-gray-500" />
                ) : (
                  <img
                    src={existingUrl}
                    alt="Existing Document"
                    className="h-16 w-16 object-cover rounded"
                  />
                )}
                <a
                  href={existingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  View Existing Document
                </a>
                <button
                  type="button"
                  onClick={() => {
                    setExistingUrl(null);
                    setIsEditing(true);
                  }}
                  className="text-yellow-500 hover:text-yellow-700"
                >
                  <Pencil className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={!document}
            className={`w-full ${document ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-300 cursor-not-allowed'} text-white font-semibold py-2 px-4 rounded-md flex items-center justify-center gap-2`}
          >
            <CheckCircle className="w-5 h-5" />
            Submit Application
          </button>
        </form>
      )}
    </div>
  );
};

export default TDSApplicationPage;
